<!-- Ported from the Polaris kit: src/product/components/FileAttachment.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first. -->
<script setup>
// A file in a thread or a composer, drawn by what it is: each kind has its own color and icon (pdf red, doc blue, sheet green, csv
// yellow, slides orange, archive purple, text grey, markdown teal, json amber, code indigo, image pink, audio cyan, video magenta).
// Built on the registry attachment; the kind comes from the extension. layout "chip" is a row, "card" is a tile with the extension on
// a colored banner and a small page under it.
import MiddleTruncate from './MiddleTruncate.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CloudUpload16, Renew16, TrashCan16 } from '@carbon/icons-vue';
import { KINDS } from './fileKinds';
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from '../ui/attachment';

const props = defineProps({
  name: { type: String, required: true },
  // Optional: when the caller already knows the real extension (e.g. from the attachment record,
  // not parsed off a possibly query-stringed URL), pass it here so the kind/icon don't fall back
  // to "other". Defaults to parsing it off `name`, same as before.
  extension: { type: String, default: '' },
  description: { type: String, default: '' },
  layout: { type: String, default: 'chip' },
  state: { type: String, default: 'done' },
  actions: { type: Boolean, default: false },
});
const { t } = useI18n();
const resolvedExtension = computed(() => {
  if (props.extension) return props.extension.toLowerCase();
  return props.name.includes('.')
    ? props.name.split('.').pop().toLowerCase()
    : '';
});
const kind = computed(() =>
  props.state === 'idle'
    ? 'other'
    : Object.keys(KINDS).find(k =>
        KINDS[k].ext.includes(resolvedExtension.value)
      ) || 'other'
);
const icon = computed(() =>
  props.state === 'idle' ? CloudUpload16 : KINDS[kind.value].icon
);
const hint = computed(() => props.description || t(KINDS[kind.value].labelKey));
</script>

<template>
  <Attachment
    :state="state"
    :orientation="layout === 'card' ? 'vertical' : 'horizontal'"
    :data-file-kind="kind"
  >
    <AttachmentMedia
      v-if="layout === 'chip'"
      class="file-tile"
      :data-file="kind"
    >
      <component :is="icon" aria-hidden="true" />
    </AttachmentMedia>
    <AttachmentMedia
      v-else
      class="file-tile !aspect-auto !h-auto flex-col items-stretch justify-start gap-0 p-0"
      :data-file="kind"
    >
      <span
        class="type-polaris-caption flex items-center gap-1 px-2 py-1.5 uppercase"
        ><component :is="icon" class="!size-3.5 p-0" aria-hidden="true" />{{
          resolvedExtension || 'file'
        }}</span
      >
      <span
        class="file-page mx-2 mb-2 group-data-[state=idle]/attachment:hidden"
        aria-hidden="true"
        ><span /><span /><span
      /></span>
    </AttachmentMedia>
    <AttachmentContent>
      <AttachmentTitle><MiddleTruncate :text="name" /></AttachmentTitle>
      <AttachmentDescription>
        {{ hint }}
      </AttachmentDescription>
    </AttachmentContent>
    <AttachmentActions v-if="actions && state !== 'idle'">
      <!-- A caller with its own actions (download, jump to message, ...) overrides this default
           retry/remove pair via the named slot; the kit gallery and any caller that doesn't need
           custom actions keep this. -->
      <slot name="actions">
        <AttachmentAction
          v-if="state === 'error'"
          :aria-label="t('POLARIS.FILE_ATTACHMENT.RETRY', { name })"
        >
          <Renew16 aria-hidden="true" />
        </AttachmentAction>
        <AttachmentAction
          :aria-label="t('POLARIS.FILE_ATTACHMENT.REMOVE', { name })"
        >
          <TrashCan16 aria-hidden="true" />
        </AttachmentAction>
      </slot>
    </AttachmentActions>
  </Attachment>
</template>
